import React, { useCallback, useEffect, useState } from "react";
import { journalAPI, shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { formatClock, formatDate } from "../utils/dates";
import { describeEntry, JOURNAL_CATEGORIES } from "../utils/journal";
import DateRangeFilter, { rangeFor } from "../components/DateRangeFilter";
import EmptyState from "../components/EmptyState";

const PAGE_SIZE = 50;

/**
 * Everything that happened in the shop: who did it, when, on which device.
 *
 * The owner's check on the shop when they are not in it — sales, refunds,
 * stock counts, price changes, tab payments, the till being opened and every
 * wrong PIN. Owner-only and read-only: nothing here can be edited or removed.
 */
const Journal = () => {
  const { t } = useI18n();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);

  const [range, setRange] = useState(() => rangeFor("today"));
  const [actorId, setActorId] = useState("");
  const [category, setCategory] = useState("");
  const [people, setPeople] = useState([]);
  const [entries, setEntries] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    shopsAPI
      .staff()
      .then(({ data }) => setPeople(data))
      .catch(() => setPeople([]));
  }, []);

  const fetchPage = useCallback(
    (before) =>
      journalAPI.list({
        start_date: range.start_date,
        end_date: range.end_date,
        actor_id: actorId || undefined,
        category: category || undefined,
        before,
        limit: PAGE_SIZE,
      }),
    [range.start_date, range.end_date, actorId, category],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchPage()
      .then(({ data }) => {
        if (cancelled) return;
        setEntries(data);
        setHasMore(data.length === PAGE_SIZE);
        setError("");
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || t("journal.loadFailed"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await fetchPage(entries[entries.length - 1].cursor);
      setEntries((current) => [...current, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      setError(err.response?.data?.message || t("journal.loadFailed"));
    } finally {
      setLoadingMore(false);
    }
  };

  // Grouped under the day they happened, newest first
  const days = entries.reduce((groups, entry) => {
    const day = formatDate(entry.created_at);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.entries.push(entry);
    else groups.push({ day, entries: [entry] });
    return groups;
  }, []);

  const select =
    "px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700";

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">
            {t("journal.title")}
          </h1>
          <p className="text-gray-600">{t("journal.subtitle")}</p>
        </div>

        <DateRangeFilter value={range} onChange={setRange} />

        <div className="flex flex-wrap gap-3">
          <label className="text-sm text-gray-600">
            <span className="block mb-1">{t("journal.person")}</span>
            <select value={actorId} onChange={(e) => setActorId(e.target.value)} className={select}>
              <option value="">{t("journal.everyone")}</option>
              {people.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            <span className="block mb-1">{t("journal.type")}</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={select}>
              <option value="">{t("journal.everything")}</option>
              {JOURNAL_CATEGORIES.map((name) => (
                <option key={name} value={name}>
                  {t(`journal.categories.${name}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-primary-600" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState screen="journal" message={t("journal.empty")} />
      ) : (
        days.map(({ day, entries: dayEntries }) => (
          <section key={day} className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
              {day}
            </h2>
            <ul className="divide-y divide-gray-100">
              {dayEntries.map((entry) => {
                const { icon, title, lines, warn } = describeEntry(entry, { t, money });

                return (
                  <li key={entry.id} className="flex gap-3 py-3">
                    <span className="shrink-0 w-8 text-xl leading-6 text-center" aria-hidden="true">
                      {icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`font-medium ${warn ? "text-amber-800" : "text-gray-900"}`}>
                        {title}
                      </p>
                      {lines.map((line, index) => (
                        <p key={index} className="text-sm text-gray-600 break-words">
                          {line}
                        </p>
                      ))}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {entry.actor_name || t("journal.unknownPerson")}
                        {entry.device_label && ` · ${entry.device_label}`}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm text-gray-500 tabular-nums" dir="ltr">
                      {formatClock(entry.created_at)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}

      {hasMore && !loading && (
        <div className="text-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="px-5 py-2.5 rounded-lg border border-gray-300 bg-white font-semibold hover:bg-gray-50 disabled:opacity-60"
          >
            {loadingMore ? t("common.loading") : t("journal.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
};

export default Journal;
